import {
  memo,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Toaster } from "react-hot-toast";
import { IntlProvider } from "react-intl";
import type { LinksFunction, ShouldRevalidateFunction } from "react-router";
import {
  Links,
  Meta,
  Outlet,
  ScrollRestoration,
  UNSAFE_DataRouterContext,
  UNSAFE_DataRouterStateContext,
  isRouteErrorResponse,
  useLoaderData,
  useLocation,
  useMatches,
  useRouteError,
  useRouteLoaderData,
} from "react-router";

import { getContext } from "./.server/context.server";
import {
  getSessionServerContext,
  optionalAuthenticatedUser,
} from "./.server/sessions.server";
import { getTimingContext } from "./.server/timing.server";
import { logBrokenLoyaltyScope } from "./hec/.server/annex-loyalty.server";
import {
  deriveCustomerGroups,
  getAfidFromRequest,
  isSegmentMarketingSuppressedForTpa,
} from "./hec/helpers/tpa.server";
import { peekWarmManifestParsed } from "./lib/abtasty-manifest-slot.server";
import {
  parseBuilderUserAttributesCookie,
  reconcileBuilderCookieAttrs,
} from "./lib/builder-attrs-reconcile.server";
import { composeBuilderUserAttributes } from "./lib/builder-user-attributes-compose.server";
import {
  buildPersonaDebugFlowInputs,
  resolvePersonalizationDebug,
} from "./lib/personalization-debug.server";
import { buildPersonalizationDevConfig } from "./lib/personalization-devconfig.server";
import { buildRedirectTestDebug } from "./lib/redirect-test.server";
import { fetchTranslationsFromKv } from "./lib/utils-server/locale.server";
import {
  isSmartSpenderEnrolled,
  resolveSmartSpenderState,
} from "./routes/my-account/smart-spender.server";

import { setClientUserAttributes } from "@builder.io/sdk-react";

import { DevTools } from "~/components/dev-tools";
import { PersonalizationRefreshTrigger } from "~/components/personalization-refresh-trigger";
import defaultConfig from "~/config/default";
import { LeanScripts } from "~/hec/components/lean-scripts";
import { cn } from "~/lib/utils";

import { AppContextMiddleware } from "./.server/middleware/app-context.middleware";
import { CacheMiddleware } from "./.server/middleware/document-cache.middleware";
import { MemoryForensicsMiddleware } from "./.server/middleware/memory-forensics.middleware";
import { PageTreeReloadSignalMiddleware } from "./.server/middleware/page-tree-reload-signal.middleware";
import { PrefetchCacheMiddleware } from "./.server/middleware/prefetch-cache.middleware";
import { RedirectTestMiddleware } from "./.server/middleware/redirect-test.middleware";
import { SensitiveNoStoreMiddleware } from "./.server/middleware/sensitive-no-store.middleware";
import { SessionManagementMiddleware } from "./.server/middleware/session-management.middleware";
import { TimingMiddleware } from "./.server/middleware/timing.middleware";
import { SEO_VERIFICATION } from "./config/seo-verification.config";
import { getSeoConfig } from "./config/seo.config";
import { SiteProvider } from "./contexts";
import { getAbTastyConfig } from "./hec/.server/ab-tasty-config";
import { getBloomreachConfig } from "./hec/.server/bloomreach-config";
import { getBuilderConfig } from "./hec/.server/config";
import {
  getCIOConfig,
  resolveCioSdkTrackingEnabled,
  resolveCioServiceUrl,
  toClientServiceUrl,
} from "./hec/.server/constructor-config";
import { getFreeShipping } from "./hec/.server/free-shipping";
import { getGtmConfig } from "./hec/.server/gtm-config";
import { resolvePageType } from "./hec/.server/page-type";
import { getGtmRevShare } from "./hec/.server/rev-share-config";
import { getClientSafePreferenceSafe } from "./hec/.server/site-preferences";
import { getZendeskSnippetSrc } from "./hec/.server/zendesk-config";
import { AbTastyPageviewTracker } from "./hec/analytics/abtasty-pageview-tracker";
import { BloomreachScripts } from "./hec/analytics/bloomreach";
import {
  CioConversionTracker,
  CioSearchTermScope,
  CioSessionStartTracker,
} from "./hec/analytics/cio-sdk-trackers";
import { CioSdkTrackingContext } from "./hec/analytics/cio-sdk-tracking";
import type { ClickSource } from "./hec/analytics/click-source";
import { pushDataLayer, pushPageType } from "./hec/analytics/data-layer.client";
import type { Ga4ProductDetailItem } from "./hec/analytics/ga4-product-detail";
import {
  AbTastyAsyncTagInlineScript,
  ConsentDefaultsInlineScript,
  DataLayerClientPushes,
  FlagshipVidInlineScript,
  GtmContainerScript,
  PageTypeInlineScript,
  PzHitsConfigInlineScript,
  TrustArcBootstrapInlineScript,
} from "./hec/analytics/gtm-inline";
import { encodeHeapId } from "./hec/analytics/heap-id";
import { HecSrcInlineScript } from "./hec/analytics/hec-src-inline";
import { IdentityTracker } from "./hec/analytics/identity-tracker";
import {
  INITIAL_NAVIGATION_ORDINAL,
  type PageContext,
  buildPageContext,
  documentIdentityFrom,
  navigationIdAt,
  navigationKey,
} from "./hec/analytics/page-context";
import {
  type PageContextPublisher,
  createPageContextPublisher,
} from "./hec/analytics/page-context-publisher";
import {
  matchedSeo,
  resolvePageTitle,
  seoFromRouterState,
} from "./hec/analytics/page-title";
import {
  boundaryError,
  fallbackFromRootData,
  layoutPageType,
  matchedPageType,
  notFoundFromRootData,
  pageTypeFromRouterState,
} from "./hec/analytics/page-type-selection";
import {
  PersonaDebugDevEcho,
  PersonaDebugInlineScript,
} from "./hec/analytics/persona-debug-inline";
import { ProductDetailTracker } from "./hec/analytics/product-detail-tracker";
import { RouteChangePerfTracker } from "./hec/analytics/route-change-perf-tracker";
import { useHecSrcSoftNavRefresh } from "./hec/analytics/use-hec-src-soft-nav-refresh";
import { isIntakePath } from "./hec/analytics/zendesk";
import { ZendeskChatFacade } from "./hec/analytics/zendesk-chat-facade";
import { BuilderEditorLinkGuard } from "./hec/components/builder-editor-link-guard";
import { BuilderPreviewBanner } from "./hec/components/builder-preview-banner";
import { RevShareLinkTracker } from "./hec/components/rev-share-link-tracker";
import { TpaSiteSwitchTracker } from "./hec/components/tpa-site-switch-tracker";
import { DocumentNavGuardInlineScript } from "./hec/components/ui/document-nav-guard-inline";
import {
  isEnabled,
  isEnabledForSite,
  resolveAbTastyAsyncTagMode,
  resolveIntegrationFlags,
  resolveMyAccountMode,
  resolveOrdersMode,
} from "./hec/config/integration-flags";
import { TRUSTARC_DOMAINS, TRUSTARC_HOST } from "./hec/config/trustarc";
import { useRestoreTpaBranding } from "./hec/hooks/use-restore-tpa-branding";
import {
  legacyListPatternSources,
  legacyRoutePatternSources,
} from "./hec/legacy-routes";
import {
  isOptedIn,
  loyaltyPoints,
  parseLoyaltyData,
} from "./hec/loyalty/loyalty-data.logic";
import { isPerksMutationSubmit } from "./hec/loyalty/perks-refresh.logic";
import { useIsPprDocument } from "./hec/ppr/ppr-document";
import { PprHole } from "./hec/ppr/ppr-hole";
import { ABS_CONSENT_ACTION } from "./hec/smart-spender/smart-spender.logic";
import { TPA_ESCAPE_INLINE_SOURCE } from "./hec/tpa/tpa-escape-runtime";
import { safeSetClientUserAttributes } from "./hec/utils/builder-user-attributes";
import { BuilderUserAttributesInlineScript } from "./hec/utils/builder-user-attributes-inline";
import { type SiteId, bySiteId, siteIdToCode } from "./hec/utils/site-id";
import { campaignFlagEntries } from "./lib/abtasty-builder-flags";
import { isBuilderEditorRequest } from "./lib/builder-editor-request";
import { DEFAULT_COUNTRY_CODES } from "./lib/constants";
import { getCountryByCode, getCountryByLocale } from "./lib/countries";
import { ClientOnly } from "./lib/ui";
import { getHints } from "./lib/utils/client-hints";
import { createUrlTemplate, getDomainUrl, originOf } from "./lib/utils/url";
import { invariant } from "./lib/utils/utils";
import {
  isAuthRevalidatingSubmit,
  isLeavingLoginPage,
} from "./routes/auth-revalidate";
import type { loader as layoutLoader } from "./routes/layout";
import { pdpDataFromMatch } from "./routes/pdp-data-from-match";
import { Segment } from "./segment";
import { CartUpdateTracker } from "./segment/cart-update-tracker";
import { SegmentDataLayerInterceptor } from "./segment/data-layer-interceptor-installer";
import { SegmentIdentify } from "./segment/identify";
import { SegmentLogoutReset } from "./segment/logout-reset";
import { SegmentPageView } from "./segment/page-view-tracker";
import { TpaConsentTracker } from "./segment/tpa-consent-tracker";
import { useLearningCenterNavTracker } from "./segment/use-learning-center-nav-tracker";
import { hashEmail } from "./segment/utils";
import { DeferH1Seo, DeferSeo } from "./seo/seo";
import { isTpaRequest } from "./seo/utils/tpa-request";
import { parsePriceTestVariant } from "./shop/price-test-variant";
import "./styles/app.css";
import "./styles/custom-fonts.css";
import "./styles/radix-themes/colors-light.css";
import "./styles/radix-themes/radius.css";
import "./styles/radix-themes/scaling.css";
import "./styles/radix-themes/storefront.css";
import "./styles/radix-themes/theme.css";
import "./styles/swiper.css";
import { CIOClientContext } from "./utils/constructor";
// Type-only namespace import — erased at compile time, so it does NOT put the
// CIO SDK chunk back on the critical path (COM-160). The runtime module only
// arrives via the deferred `import()` in App below.
import type * as CioClientModule from "./utils/constructor.client";

// Written by the inline <script> in Layout's <head>; read by the deferred
// CIO beacon and `app/utils/constructor.client.ts`. See spike §3.3.
declare global {
  interface Window {
    cnstrcUserId?: string;
    // Written by FlagshipVidInlineScript (top of <body>, see Layout below) —
    // the AB Tasty Universal Collect vid, for client-side GTM Event hits
    // (MarTech's scope) to reuse the same vid the Worker's PAGEVIEW/
    // TRANSACTION/ITEM hits use (SFCC-11846 guide §6).
    __FLAGSHIP_VID?: string;
    // COM-585: the current page context. Seeded by PageTypeInlineScript at
    // first paint and kept in step by the committed-route publisher, so
    // route-bound event stamping always has this route's context to read —
    // including before any React tracker has mounted.
    __HEC_PAGE_CTX?: PageContext;
  }
}

// #region Middleware
// Middleware composition: AppContext must run first to set up contexts,
// then SessionManagement can use those contexts
export const middleware = [
  // Outermost on purpose (touches no context): COM-323 forensics must wrap
  // the FINAL outgoing body — including edge-cache HIT/MISS streams — so a
  // request counts as in-flight until its body actually drains, not until
  // headers are returned. See memory-forensics.middleware.ts.
  MemoryForensicsMiddleware,
  AppContextMiddleware,
  // Position 2 is load-bearing (docs/personalization-redirect-tests-plan.md
  // §4.3): needs AppContext's `abtastyEvaluation`, and must sit OUTSIDE
  // PrefetchCache/Cache so a personalized 302 can never be cached and the
  // redirect decision runs before an edge-cache HIT could serve the
  // canonical document. Throws RR `redirect()` (never a raw worker-seam 302
  // — the PR #1039 single-fetch black-screen class). Inert while
  // REDIRECT_TEST_PATHS is "" (fail-closed, every tier today).
  RedirectTestMiddleware,
  // COM-403: immediately OUTSIDE PrefetchCacheMiddleware — the one layer that
  // rewrites Cache-Control on the way out (`private, max-age=300` on prefetch
  // GETs) — so per-shopper routes (`SENSITIVE_PATH_PREFIXES`,
  // ~/lib/sensitive-paths) leave the handler as `private, no-store` on EVERY
  // response shape: document, `.data`, action result, thrown redirect, error
  // page, prefetch. Needs no context, so any index ≤ 3 works; kept inside
  // RedirectTest so its "position 2" invariant above stays literal —
  // RedirectTest only ever tightens to `no-store` itself, and AppContext /
  // MemoryForensics never touch Cache-Control.
  SensitiveNoStoreMiddleware,
  // Runs after AppContext (needs its context for the auth check) and outside the
  // edge cache, so it has the final say on the client Cache-Control for
  // speculative prefetches (SFCC-11297) — except redirect-test entry paths,
  // where RedirectTestMiddleware (outer) forces prefetches to no-store, and
  // per-shopper account / orders / payments paths, where
  // SensitiveNoStoreMiddleware (outer, COM-403) forces every response to
  // `private, no-store`.
  PrefetchCacheMiddleware,
  TimingMiddleware,
  // OUTSIDE CacheMiddleware on purpose (PR #1120 review round 1): consumes the
  // reload-carried `hec_ptf` page-tree failure signal — one warn + a clearing
  // Set-Cookie. The SWR doc-cache key ignores cookies, so a cookie-bearing
  // reload can be a HIT that never reaches the inner middlewares; a consumer
  // inside the cache would neither log nor clear, and the lingering cookie
  // would keep the shopper out of the cookieless guest HTML cache.
  PageTreeReloadSignalMiddleware,
  CacheMiddleware(() => import.meta.env.MODE === "production"),
  SessionManagementMiddleware,
];
// #endregion

/* eslint-disable react-refresh/only-export-components */
export { RootErrorBoundary as ErrorBoundary } from "~/components/error-boundries/root-error-boundary";
export const loader = async (args: LoaderArgs) => {
  const { createTimer } = getTimingContext();
  const [endLoaderTimer] = createTimer(
    "root-loader-total",
    "Root loader total",
  );
  try {
    return await loaderImpl(args);
  } finally {
    endLoaderTimer();
  }
};

const loaderImpl = async ({ request, context }: LoaderArgs) => {
  const { locale, currency, site, lang, cspNonce } = context;

  // SFCC-11018 (ANALYTICS-30): TPA/ISE iframe context. Computed up front so the
  // Zendesk prefs fetch can be skipped on TPA — `resolveZendeskSnippetSrc`
  // discards the result there anyway, and TPA hosts are exactly where the extra
  // AM-token + Preferences-API round-trip (cold isolate) hurts latency. Also
  // drives the SEO robots block below.
  const isTpa = isTpaRequest(request);

  // Hoisted so the Builder userAttributes composition below reads the same
  // parsed header instead of re-reading it.
  const cookieHeader = request.headers.get("Cookie") ?? "";

  // AB Tasty Universal Collect `vid` — resolved ONCE per request in
  // worker.server.ts (before RR7 dispatch) and threaded via
  // cloudflareContext/AsyncServerContext, NOT re-derived here: the Worker's
  // queue-produced hits (ACTIVATE, TRANSACTION/ITEM, checkout EVENT) and this
  // SSR `window.__FLAGSHIP_VID` injection (which the D6 client sender reads)
  // must agree on the exact same value, including when it was freshly minted
  // (no prior cookie) — re-resolving independently here could mint
  // a second, different UUID for the same request.
  const abTastyVid = context.abTastyVid.vid;

  // Both hit KV; resolve in parallel so the notfound page-type lookup doesn't
  // serialize after translations. `notFoundPageType` is consumed synchronously
  // by `Layout`'s inline `<head>` script (must precede GTM), so it has to be a
  // resolved string by the time the loader returns.
  const [
    translations,
    notFoundPageType,
    fallbackPageType,
    customer,
    zendeskSnippetSrc,
    gtmRevShare,
    traits,
    abtastyEvaluation,
    zeroStateSearchPref,
    freeShipping,
    cioServiceUrl,
    cioSdkTrackingEnabled,
  ] = await Promise.all([
    fetchTranslationsFromKv(lang),
    resolvePageType("notfound"),
    // SFCC-12034 C7: the `other` bucket, for pages whose loader sets no
    // pageType. Resolved here (not hard-coded to "Other Page") so it comes
    // from the same `gtmPageTypesMapping` site preference SFRA reads — see
    // the fallback in `usePageType`'s consumers below.
    //
    // Cost: this is a SEPARATE SWR entry from the `notfound` lookup (the cache
    // key includes the key name), and `getSitePreference` has no in-flight
    // dedupe, so a cold isolate + cold cache can fetch the mapping twice.
    // Wall-clock is unaffected — both resolve inside this `Promise.all` — but
    // if the cold-path duplicate ever matters, batch the two into a single
    // `resolvePageTypes([...])` read rather than moving this out of here.
    resolvePageType("other"),
    optionalAuthenticatedUser(),
    // SFCC-11018 (ANALYTICS-30): resolved Zendesk snippet src (null when
    // suppressed). Site-preference-backed (3-tier cached, graceful); skips the
    // prefs fetch on TPA/ISE. Parallel with the other root-loader reads.
    getZendeskSnippetSrc(isTpa),
    // SFCC-11457: Builder `gtmRevShare` gate. `true` ⇒ GTM owns rev-share param
    // injection (default); `false` ⇒ mount the client-side rewrite listener.
    getGtmRevShare(getBuilderConfig(context.preset).api_key),
    context.traits,
    context.abtastyEvaluation,
    // COM-375 / SFCC-12204: per-site BM on/off for the zero-state search
    // dropdown, replacing the AB Tasty experiment flag. The pref exists in BM
    // on all three sites (FSA false, HSA/WDH true as of 2026-09-24); an absent
    // pref or a prefs blip still => ENABLED, matching `cio-autocomplete`'s
    // `?? true`.
    getClientSafePreferenceSafe<boolean | string | null>(
      "zeroStateSearchEnabled",
      false,
    ),
    // Free-shipping tracker + PDP badge thresholds/visibility from BM site
    // prefs (never throws — fails closed to hidden). Skips the fetch on TPA,
    // where SFRA suppresses the tracker anyway.
    getFreeShipping({ isTpa }),
    // COM-508: Constructor base URL from the BM `Constructor_ServiceUrl` pref
    // (load-test mock switch, SFRA parity). Feeds the browser SDK's
    // `serviceUrl`; the server-side `cio*` fetches resolve it themselves.
    resolveCioServiceUrl(),
    // SFCC-11961: BM `Constructor_SdkTrackingEnabled` — the beacon → JS SDK
    // tracking cutover switch, shared with SFRA (fails closed to the beacon).
    resolveCioSdkTrackingEnabled(),
  ]);
  // SDK-conformant view of the evaluation — the debug payload and the flatten
  // path consume this; the unshadowed per-campaign flags ride alongside on
  // `abtastyEvaluation` (see loadargs.types.ts).
  const abtastyVariationResults = abtastyEvaluation.variations;
  // COM-508: `null` on Constructor's default host → the browser SDK keeps its
  // own default and nothing is added to the document payload.
  const cioClientServiceUrl = toClientServiceUrl(cioServiceUrl);

  const preferredCountryCode = context.session.get("preferredCountryCode");
  const cnstrcUserId = context.session.get("cnstrcUserId");
  // SFCC-11025 (ANALYTICS-35): auth-state boolean for the GTM container's
  // pre-`gtm.start` push — feeds GTM audience segmentation by registered vs.
  // guest. Derived from the `customer` already resolved in the Promise.all
  // above: `optionalAuthenticatedUser()` returns `null` only for guests, so
  // `customer != null` is equivalent to `isAuthenticatedUser()` without a
  // second read.
  const isRegistered = customer != null;
  // COM-468: started here so its cold-isolate `TPAConfig` read overlaps the
  // rest of the loader; never rejects (unreadable config resolves ineligible),
  // and resolves with no I/O for guests and non-FSA sites.
  const smartSpenderState = resolveSmartSpenderState(customer, site.id);
  // SFCC-11024 (ANALYTICS-33): Heap analytics identity — `base64(email)`
  // pushed to dataLayer for authenticated customers. Reuses the `customer`
  // already fetched above instead of a duplicate `getCustomer` SCAPI call
  // (the consolidation flagged for SFCC-11009).
  const heapId = customer ? encodeHeapId(customer.email) : null;
  // SFCC-11009 (ANALYTICS-11 + ANALYTICS-24): customer identity + loyalty,
  // derived from the same `customer` already fetched above (no second
  // getCustomer). `userId` is the customer number, not the email.
  const userId = customer?.customerNo ?? null;
  // SFRA's GTM push hashes the RAW email (`dataLayerHelper.js`). We intentionally
  // reuse the lowercase-then-hash `hashEmail` so `hashedEmail` matches the
  // Segment userId for the same shopper (one hashing path across destinations);
  // this differs from SFRA only for mixed-case email addresses.
  const hashedEmail = customer?.email ? await hashEmail(customer.email) : null;
  // Persisted Annex Cloud loyalty state (`c_acLoyaltyData`) — same attribute
  // SFRA reads (loyaltyHelper), NOT a live Annex call. Absent ⇒ not enrolled.
  // Guests carry no blob: skip the parse and the broken-scope check entirely.
  const savedLoyalty = customer as { c_acLoyaltyData?: string } | null;
  const loyalty = savedLoyalty ? parseLoyaltyData(savedLoyalty, site.id) : null;
  if (customer) {
    logBrokenLoyaltyScope(
      customer.customerId,
      savedLoyalty?.c_acLoyaltyData,
      site.id,
    );
  }
  const customerLoyalty = isOptedIn(loyalty);
  const points = loyaltyPoints(loyalty);
  // customerGroupSalesforce: recreate the source-defined customer-group rules
  // from Workers-reachable state (auth, tpaBranding cookie/AFID, loyalty opt-in).
  const customerGroups = deriveCustomerGroups({
    request,
    isRegistered,
    optedIn: customerLoyalty,
  });
  // SFCC-11275 (TPA-17): server-side TPA marketing-consent context, mirroring
  // the live headless `canTrackMarketingEvents`. False for non-TPA traffic (no
  // AFID). This is no longer a first-party Segment suppression gate; callers use
  // it only to decide whether a TPA consent capture should be persisted.
  const segmentTpaSuppressed = await isSegmentMarketingSuppressedForTpa({
    request,
    learningCenterConsent:
      (customer as { c_tpaLearningCenterConsentData?: boolean } | null)
        ?.c_tpaLearningCenterConsentData === true,
  });
  // SFCC-11945: AFID presence (URL / `tpaBranding` cookie) — the composable
  // `isTpaUser` signal. `<TpaConsentTracker />` asserts the `tpa-initial-load`
  // consent for an affiliate shopper who is marketing-allowed
  // (`isTpaAffiliate && !segmentTpaSuppressed`).
  const isTpaAffiliate = getAfidFromRequest(request) !== null;
  const { env } = getSessionServerContext();
  // Enablement flags (see app/hec/config/integration-flags.ts). §5.5 as
  // re-amended 2026-09-04: the abtasty flag gates ONLY the D6 client
  // hit-sender config here (`pzHits`, injected when on); the legacy browser
  // tag rides its OWN switch (WORKERS_ABTASTY_TAG_SUPPRESS_MODE — the two
  // runtimes deliberately COEXIST: separate accounts, visitor ids, and
  // endpoints; live client-side tests must never be interrupted). Segment
  // analytics (segmentWriteKey → analytics.js) stays out of the flags'
  // scope; the server-side gates live at evaluation and the queue producers.
  const integrationFlags = resolveIntegrationFlags(env ?? {}, site.id);
  // COM-162 — fail-closed: only an explicit WORKERS_ABTASTY_ASYNC_MODE="on"
  // switches the browser AB Tasty tag to async + capped anti-flicker. Off ⇒
  // the shipped synchronous tag, byte-identical HTML. Resolved server-side and
  // serialized to Layout because the two variants render different markup.
  const abTastyAsyncTag = resolveAbTastyAsyncTagMode(env ?? {}).mode === "on";
  const segmentWriteKey = bySiteId(site.id, {
    FSASTORE: env?.SEGMENT_FSA_WRITE_KEY,
    HSASTORE: env?.SEGMENT_HSA_WRITE_KEY,
    WDH: undefined,
  });
  const IMAGES_HOST = env?.IMAGES_HOST || context.preset.imagesHost;
  // DIS image folder id (e.g. `BFKW_PRD` / `BFKW_DEV`). Preset derives it per
  // instance; an explicit `IMAGE_FOLDER_ID` env var overrides per deployment —
  // set it to "" to force-disable DIS (instant rollback to the raw static route).
  const IMAGE_FOLDER_ID = env?.IMAGE_FOLDER_ID ?? context.preset.imageFolderId;
  const TOP_BAR_MESSAGES_INTERVAL = env?.TOP_BAR_MESSAGES_INTERVAL;
  const DEFAULT_SEARCH_TERM = env?.DEFAULT_SEARCH_TERM;
  let preferredCountry =
    preferredCountryCode && getCountryByCode(preferredCountryCode);
  if ((preferredCountry && preferredCountry.locale) !== locale.id) {
    preferredCountry = getCountryByLocale(locale.id);
  }

  const requestCountryCode = DEFAULT_COUNTRY_CODES.US;
  //TODO: Redirect if request is not on the preferred site
  // const [preferredLocale, Site] = (preferredCountryCode &&
  //   getLocaleByCountry(preferredCountryCode)) || [undefined, undefined];
  // Build dev config panel data (non-production only)
  const isDevMode = (env?.ENV as string | undefined) !== "production";
  const mask = (val?: string | null) =>
    val ? val.slice(0, 4) + "••••••••" : "(not set)";
  // THS gateway creds: the same `mask` prefix as every other credential row
  // (so the value can be eyeballed against the vault), plus the length. The
  // length matters because `thsAuthHeaders` sends the value raw, with no
  // trim: a dashboard paste that picks up a trailing newline still fails the
  // gateway's credential check, and the prefix alone renders it identically
  // to a clean value. A length/trimmed-length mismatch is called out loudly.
  const credShape = (val?: string | null) => {
    if (!val) return "(not set)";
    const trimmedLength = val.trim().length;
    return trimmedLength === val.length
      ? `${mask(val)} (length ${val.length})`
      : `${mask(val)} (length ${val.length} ⚠ WHITESPACE — trimmed ${trimmedLength})`;
  };
  const devConfig = isDevMode
    ? {
        SFCC_INSTANCE:
          (env as unknown as { SFCC_INSTANCE?: string })?.SFCC_INSTANCE ??
          env?.SFCC_ENV ??
          "(not set)",
        siteId: defaultConfig.app.commerceAPI.parameters.siteId,
        organizationId: defaultConfig.app.commerceAPI.parameters.organizationId,
        shortCode: defaultConfig.app.commerceAPI.parameters.shortCode,
        scapiClientId: mask(defaultConfig.app.commerceAPI.parameters.clientId),
        scapiClientSecret: mask(env?.SFCC_SCAPI_CLIENT_SECRET),
        ocapiClientId: mask(env?.SFCC_OCAPI_CLIENT_ID),
        ocapiClientSecret: mask(env?.SFCC_OCAPI_CLIENT_SECRET),
        IMAGES_HOST,
        imageFolderId: IMAGE_FOLDER_ID || "(raw static — DIS off)",
        builderApiKey: mask(getBuilderConfig(context.preset).api_key),
        constructorApiKey: mask(getCIOConfig(context.preset).apiKey),
        // COM-508: resolved Constructor base (BM `Constructor_ServiceUrl`, or
        // the default) so QA can verify a load-test flip without Worker logs.
        constructorServiceUrl: cioServiceUrl,
        gtmContainerId: getGtmConfig(context.preset).containerId || "(not set)",
        stripeKey: mask(env?.STRIPE_PRIVATE_API_KEY),
        thsClientId: credShape(env?.THS_CLIENT_ID),
        thsClientSecret: credShape(env?.THS_CLIENT_SECRET),
        APP_ORIGIN: env?.APP_ORIGIN,
        SFRA_ORIGIN: env?.SFRA_ORIGIN,
        ENV: env?.ENV,
        // Kill-switch state incl. which layer set it ("env" vs "default") —
        // the non-prod twin of the `[integration-flags] resolved` log line.
        // tagSuppress is INVERTED (§5.5 re-amendment): off = tag renders.
        integrationFlags:
          `segmentProfileApi=${integrationFlags.segmentProfileApi} ` +
          `(${integrationFlags.source.segmentProfileApi}), ` +
          `abtasty=${integrationFlags.abtasty} ` +
          `(${integrationFlags.source.abtasty}), ` +
          `tagSuppress=${integrationFlags.abtastyTagSuppress} ` +
          `(${integrationFlags.source.abtastyTagSuppress}), ` +
          `trustArcSlimBanner=${integrationFlags.trustArcSlimBanner} ` +
          `(${integrationFlags.source.trustArcSlimBanner})`,
        // Personalization env group — masked SERVER-SIDE (devConfig ships to
        // the client in loader data; see buildPersonalizationDevConfig for
        // the per-entry masking rules incl. the SEGMENT_SPACE_ID tier caveat).
        ...buildPersonalizationDevConfig(
          env as unknown as Record<string, unknown> | undefined,
        ),
      }
    : null;

  // SFCC-11717: build the personalization debug payload (holdout + AB Tasty
  // personalization campaigns + experiments + pricing variant) when the request
  // carries `?pers_debug=true`. Parses the flag ONCE: the returned `debugMode`
  // drives the noindex below (URL never re-parsed) and `personaDebug` is
  // injected as `window.__PERSONA_TRAITS` in Layout for the external MarTech
  // widget. No consent-gating — see resolvePersonalizationDebug. Reuses the
  // already-awaited `abtastyVariationResults` and the cookie-derived price
  // variant (SFCC-10760); no extra upstream call.
  // Parsed once: the reconcile below reads it for removals and the
  // campaign-flag ledger carries forward from it on non-authoritative runs.
  const previousBuilderCookieAttrs =
    parseBuilderUserAttributesCookie(cookieHeader);
  // The resolved Builder targeting object for this request — shared
  // composition (builder-user-attributes-compose.server.ts) with the
  // /resources/personalization-debug live-refresh route, and surfaced
  // verbatim in the debug payload's `builderAttributes` (tracker's BUILDER
  // ATTRIBUTES section) so the tracker shows exactly what Builder received.
  const builderUserAttributes = composeBuilderUserAttributes({
    cookieHeader,
    traits,
    abtastyEvaluation,
    previousCookieAttrs: previousBuilderCookieAttrs,
  });

  const { debugMode, payload: personaDebug } = resolvePersonalizationDebug(
    request.url,
    traits,
    abtastyVariationResults,
    {
      pricingVariant:
        parsePriceTestVariant(request.headers.get("Cookie") ?? "") || null,
    },
    integrationFlags,
    // Flow inputs for the dev Personalization Tracker overlay — all already
    // resolved on this request. Shared assembly with the
    // /resources/personalization-debug live-refresh route (one
    // implementation, no drift).
    buildPersonaDebugFlowInputs(
      cookieHeader,
      context.abTastyVid,
      env as unknown as Record<string, unknown> | undefined,
      campaignFlagEntries(abtastyEvaluation.assigned),
      abtastyEvaluation.poisoned ?? null,
      context.pzDebug,
      // R2/R3: the tracker's redirect-test row + the manifest lint's
      // entry-path set (plan §7) — display-only recompute from the same
      // pure pieces the middleware used on this request.
      buildRedirectTestDebug(
        (env as { REDIRECT_TEST_PATHS?: string } | undefined)
          ?.REDIRECT_TEST_PATHS,
        request,
        abtastyEvaluation,
        context.isbot,
      ),
      // The resolved Builder targeting object (tracker's BUILDER ATTRIBUTES
      // section) — the SAME object returned as `builderio.userAttributes`.
      builderUserAttributes,
    ),
    // P4 §4.2a: the structural value the evaluation actually received.
    context.newUserSignal,
  );

  // Default robots for every route: noindex TPA/ISE/CDN iframe requests so
  // BM preview frames never appear in search results (SFCC-11159 / SEO-12).
  // Routes that need custom logic (PLP/search with refine params) override
  // this via their own seo.robots. Routes with no custom robots logic
  // (PDP, home, Builder pages) inherit this default automatically — new
  // routes are protected without needing an explicit per-loader TPA check.
  // `isTpa` is computed once at the top of the loader. `debugMode` (SFCC-11715)
  // also forces noindex so `?pers_debug=true` URLs never enter the index.
  const seo = {
    robots: { noIndex: isTpa || debugMode, noFollow: isTpa },
  };

  // SFCC-11609: detect the Builder editor so we can suppress the Bloomreach
  // (Exponea) email-popup web layer, which otherwise overlays the canvas and
  // blocks visual editing. The predicate (chrome-ful `/edit/*` routes + the
  // in-context editor iframe signals, deliberately NOT `builder.preview` /
  // `builder.space` — those ride shareable preview links) is SHARED with the
  // personalization middleware's ACTIVATE eligibility gate so the two
  // classifications can't drift (PR #1050 review round 2).
  const reqUrl = new URL(request.url);
  const isBuilderPreview = isBuilderEditorRequest(reqUrl);

  // In the Builder editor the global chrome (mega-nav drawer, Share-of-Wallet
  // bar, footer/page CTAs) used to eagerly prefetch every category's `.data`
  // on render — a ~30-request burst that contends with the Builder SDK's
  // editor handshake and can trip Builder's "Your site is not loading as
  // expected" timeout. Chrome links now prefetch on intent only (nav prefetch
  // storm fix), but the editor still simulates hover/focus while dragging, so
  // keep suppressing prefetch while editing, EXCEPT when the nav itself
  // is the edit target: editing the mega-nav directly must still render and
  // prefetch normally. The edited model name rides `builder.frameEditing` /
  // `builder.preview` (in-context editor) or the `/edit/:model/:id` path.
  const editedBuilderModel =
    reqUrl.searchParams.get("builder.frameEditing") ??
    reqUrl.searchParams.get("builder.preview") ??
    (reqUrl.pathname.startsWith("/edit/")
      ? decodeURIComponent(reqUrl.pathname.split("/")[2] ?? "")
      : null);
  const NAV_EDIT_MODELS = ["nav-drawer-content", "affiliate-nav-bar"];
  const suppressChromePrefetch =
    isBuilderPreview && !NAV_EDIT_MODELS.includes(editedBuilderModel ?? "");
  // Stale-attribute reconcile (2026-08-25 Kimberly call): on an
  // authoritative evaluation, ledgered campaign-flag keys mirror THIS
  // request's assignments exactly (D10 — a value the visitor no longer
  // qualifies for must stop applying) and legacy persVariant keys are
  // retired unconditionally; the abtasty id trio is checked against the
  // live manifest (keep-if-live / remove-if-dead / never-wipe-on-unknown).
  // Trait-derived keys always reconcile against this request's hecTraitKeys
  // ledger — dead keys are deleted on hydration.
  const { removals: builderAttrRemovals } = reconcileBuilderCookieAttrs({
    cookieAttrs: previousBuilderCookieAttrs,
    freshAttrs: builderUserAttributes,
    manifest: peekWarmManifestParsed(),
    evaluationAuthoritative: abtastyEvaluation.authoritative,
  });

  // Same data Builder's `fetchOneEntry` calls target for this request, logged
  // for Observability. `vid` rides along so this can be cross-referenced with
  // the AB Tasty hit logs for the same visitor/request. COM-124: log attribute
  // KEYS + count only — never values. Trait values are shopper PII (names,
  // emails) and must not reach Workers Logs; inspect the merged values via
  // `?pers_debug=true` (client-only window.__PERSONA_TRAITS) instead. The vid
  // is truncated to the same 8-char prefix the [segment-traits] lines use:
  // when the ajs_anonymous_id / ajs_user_id cookies exist, vid IS that raw id
  // (resolveOrMintAbTastyVid), i.e. the full Segment profile lookup key — a
  // prefix still cross-references the AB Tasty hit logs.
  const builderAttributeKeys = Object.keys(builderUserAttributes);
  console.log(
    "[builder-attrs] userAttributes for request",
    JSON.stringify({
      vid: abTastyVid ? `${abTastyVid.slice(0, 8)}…` : abTastyVid,
      attributeKeys: builderAttributeKeys,
      attributeCount: builderAttributeKeys.length,
    }),
  );

  return {
    locale,
    cspNonce,
    isBuilderPreview,
    // COM-184: TrustArc SSR bootstrap (per-site `domain` registration; null ⇒
    // no SSR injection — WDH). Consumed by TrustArcBootstrapInlineScript in
    // Layout's body inline-script run.
    trustArcDomain: bySiteId(site.id, TRUSTARC_DOMAINS),
    // COM-184: flag-gated (WORKERS_TRUSTARC_SLIM_BANNER_MODE, fail-closed
    // default off) mobile CSS size-bounding of the consent banner. Ships dark;
    // enabling requires legal/compliance sign-off — see
    // docs/integration-kill-switches.md.
    trustArcSlimBanner: isEnabled(integrationFlags.trustArcSlimBanner),
    suppressChromePrefetch,
    currency,
    site,
    lang,
    translations,
    seo,
    // SFCC-11717: null unless `?pers_debug=true`. Injected as
    // `window.__PERSONA_TRAITS` in Layout for the external MarTech widget.
    personaDebug,
    // P4 §4.5: SSR's refresh-wanted signal for the client trigger.
    pzRefreshWanted: context.pzRefreshWanted,
    APP_DEFAULT_LANG,
    IMAGES_HOST,
    IMAGE_FOLDER_ID,
    TOP_BAR_MESSAGES_INTERVAL,
    DEFAULT_SEARCH_TERM,
    devConfig,
    notFoundPageType,
    abTastyVid,
    fallbackPageType,
    cnstrcUserId,
    isRegistered,
    heapId,
    userId,
    hashedEmail,
    customerLoyalty,
    loyaltyPoints: points,
    customerGroups,
    customer,
    // COM-468: Builder `state.smartSpenderEnrolled`, as the dashboard card shows it.
    smartSpenderEnrolled: isSmartSpenderEnrolled(await smartSpenderState),
    // BM-pref-driven free-shipping surfaces (tracker banner + PDP badge).
    freeShipping,
    segmentTpaSuppressed,
    isTpaAffiliate,
    segmentWriteKey,
    // SFCC-11457: `false` ⇒ render RevShareLinkTracker (custom client rewrite).
    gtmRevShare,
    /**
     * SFRA-owned route patterns serialized from the Worker's EFFECTIVE static
     * list (`legacyRoutePatternSources` — `LEGACY_ROUTE_PATTERN_SOURCES` plus
     * `^/my-account…` whenever this tier's `WORKERS_MY_ACCOUNT_MODE` is not
     * `on`, COM-21; same selection `worker.server.ts` `sfraPatternsFor` makes,
     * so server proxying and client link behaviour cannot disagree) PLUS the
     * runtime `LEGACY_LIST` entries
     * (as anchored, case-insensitive sources — `legacyListPatternSources`).
     * Drives `<HybridLink>` / `useIsLegacyHref` / `PrefetchBuilderAnchors` so
     * client navigation to a SFRA-owned path triggers a full document load
     * (and therefore hits `worker.server.ts:proxyToSFRA`) instead of
     * SPA-routing into a missing React Router route. Including `LEGACY_LIST`
     * keeps the no-deploy rollback lever honest client-side: appending
     * `/my-account` (COM-21) makes every `<Link to="/my-account">` — e.g.
     * `/my-expenses/order-history`'s "Back to my account" — a document
     * navigation again instead of a `/my-account.data` fetch, which on a host
     * whose eCDN still routes `/my-account*` to SFRA would be answered with
     * HTML (turbo-stream decode error) rather than route data.
     */
    legacyRoutePatterns: [
      ...legacyRoutePatternSources({
        myAccountWorkers: isEnabled(resolveMyAccountMode(env ?? {}).mode),
        // COM-47: same contract for `/orders*` + `/order-detail`.
        ordersWorkers: isEnabled(resolveOrdersMode(env ?? {}).mode),
      }),
      ...legacyListPatternSources(env?.LEGACY_LIST),
    ] as readonly string[],
    builderio: {
      config: getBuilderConfig(context.preset),
      userAttributes: builderUserAttributes,
      // Our-namespace cookie keys whose campaign is no longer live — deleted
      // by the hydration write below (see builder-attrs-reconcile.server.ts).
      attrRemovals: builderAttrRemovals,
    } as const,
    constructorio: {
      config: {
        ...getCIOConfig(context.preset),
        // Present only when BM overrides Constructor's default host, so the
        // SDK keeps its own default otherwise (SFCC-3758's rule: only pass
        // `serviceUrl` when set) and the default document payload is
        // byte-identical to before COM-508.
        ...(cioClientServiceUrl ? { serviceUrl: cioClientServiceUrl } : {}),
        // SFCC-11961: with SDK tracking on the beacon is not injected, and
        // the events only it sent go through the SDK (`cio-sdk-trackers`).
        ...(cioSdkTrackingEnabled ? { beaconBundleName: null } : {}),
        sdkTrackingEnabled: cioSdkTrackingEnabled,
      },
    } as const,
    gtm: {
      config: getGtmConfig(context.preset),
    } as const,
    abTasty: {
      // §5.5 (re-amended 2026-09-04): the legacy tag rides its OWN switch —
      // only an explicit WORKERS_ABTASTY_TAG_SUPPRESS_MODE="on" suppresses
      // it; WORKERS_ABTASTY_MODE (evaluation/hits/vid/sender) no longer
      // touches it. Tag + D6 sender coexist by design (separate accounts,
      // ids, endpoints) — the live client-side tests on the tag must never
      // be interrupted by the Worker-surface flip.
      config: getAbTastyConfig(context.preset, {
        tagSuppressed: isEnabled(integrationFlags.abtastyTagSuppress),
      }),
      /**
       * COM-162 — `WORKERS_ABTASTY_ASYNC_MODE=on`. Async tag + 500 ms-capped
       * anti-flicker instead of the synchronous parser-blocking tag.
       * Fail-closed `false` everywhere until product/martech signs off.
       */
      asyncTag: abTastyAsyncTag,
    } as const,
    /**
     * D6 client hit-sender config (`window.__PZ_HITS` — see
     * `PzHitsConfigInlineScript`). Null unless the flag is on for a genuine
     * shopper request: bots don't get a sender, and the Builder editor
     * suppresses it like the rest of the martech stack (an editor iframe
     * load is not a page view).
     */
    /**
     * COM-585 page context v2. The flag / site-allowlist / bot gates already
     * ran in `app.server.ts` (a null `documentId` means suppressed); this adds
     * the Builder-editor gate, which is only knowable here — an editor iframe
     * load is not a page view, the same reason GTM / AB Tasty / TrustArc /
     * hec_src are all suppressed there.
     *
     * `documentUrl` is captured from the REQUEST rather than rebuilt on the
     * client so `hec_page_location` is exactly the URL that was served,
     * original query-value casing included, as the contract requires.
     */
    pageContext:
      context.pageContext.documentId && !isBuilderPreview
        ? {
            documentId: context.pageContext.documentId,
            mode: context.pageContext.mode,
            documentUrl: request.url,
          }
        : null,
    pzHits:
      // Per-site AND (WORKERS_PERSONALIZATION_SITES): mode on AND this
      // request's site allowlisted — a site excluded from the allowlist gets
      // no client hit sender even while the mode is on block-wide.
      isEnabledForSite(integrationFlags, "abtasty", site.id) &&
      !context.isbot &&
      !isBuilderPreview &&
      env?.ABTASTY_ENV_ID &&
      abTastyVid
        ? ({
            cid: env.ABTASTY_ENV_ID,
            ...(context.abTastyVid.cuid
              ? { cuid: context.abTastyVid.cuid }
              : {}),
          } as { cid: string; cuid?: string })
        : null,
    featureFlags: {
      // The generic on the prefs read is an assertion, not a coercion, so a BM
      // attribute typed String would hand us "false" — truthy. Coerce here
      // (same shape as `isLmnCatalogEnabled`, tpa.server.ts:84).
      zeroStateSearch:
        zeroStateSearchPref !== false && zeroStateSearchPref !== "false",
    } as const,
    bloomreach: {
      config: getBloomreachConfig(context.preset),
    } as const,
    // SFCC-11018 (ANALYTICS-30): null when the Zendesk widget is suppressed.
    zendeskSnippetSrc,
    requestInfo: {
      ismobile: context.ismobile,
      requestCountryCode,
      isEUCountry: getContext().request.cf?.isEUCountry,
      hints: getHints(request),
      origin: getDomainUrl(request),
      /** *preferred country from session* or request country if not set */
      preferredCountry:
        preferredCountry || getCountryByCode(requestCountryCode),
      /** actual request country */
      country: requestCountryCode
        ? getCountryByCode(requestCountryCode)
        : undefined,
    },
  };
};

export const shouldRevalidate: ShouldRevalidateFunction = ({
  formMethod,
  formAction,
  formData,
  actionStatus,
  actionResult,
  currentParams: { locale: currentLocale },
  nextParams: { locale: nextLocale },
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}) => {
  // Honor an explicit `useRevalidator().revalidate()` (no form submit + same
  // URL) so the logout flow, TPA consent and the perks block can refresh root
  // chrome in place. Cross-page navigations still use the rules below.
  if (
    !formMethod &&
    currentUrl.href === nextUrl.href &&
    defaultShouldRevalidate
  ) {
    return true;
  }
  if (["setCountry"].includes(formData?.get("action")?.toString() || "")) {
    return true;
  }
  // COM-468: enrolment changes `smartSpenderEnrolled`.
  if (formAction === ABS_CONSENT_ACTION && !!actionStatus && actionStatus < 400)
    return true;
  // SFCC-11869: refresh root-loader auth state after a drawer sign-in/registration,
  // and after the sign-out POST. D4 made /logout a POST action; a form POST never
  // puts /logout into currentUrl/nextUrl — it only appears as formAction — so the
  // URL-based authPaths rule below cannot see it (shared with layout.tsx so the
  // two cannot drift).
  if (isAuthRevalidatingSubmit({ formMethod, formAction, actionStatus })) {
    return true;
  }
  // COM-798: a deadline-tracker save or Learning Center award that granted
  // points moved the saved balance and tier the header and analytics read
  // (shared with layout.tsx so the two cannot drift).
  if (
    isPerksMutationSubmit({
      formMethod,
      formAction,
      actionStatus,
      actionResult,
    })
  ) {
    return true;
  }
  // Leaving the `/account` login page (COM-7 at its canonical URL, .16): the
  // page's own loader redirects a freshly signed-in shopper to `/my-account`,
  // which short-circuits the post-login revalidation round — so THIS
  // navigation must refresh root or the chrome stays guest (see the predicate).
  if (isLeavingLoginPage({ currentUrl, nextUrl })) {
    return true;
  }
  // Revalidate when crossing auth boundaries so customer data stays in sync
  // (e.g., session expiry discovered mid-session, or navigating away after logout).
  // Still needed for the legacy GET /logout bookmark redirect (the loader redirects
  // home without mutating); the sign-out POST is matched by the formAction branch above.
  const authPaths = ["/login", "/logout"];
  if (
    authPaths.some(
      p => currentUrl.pathname.startsWith(p) || nextUrl.pathname.startsWith(p),
    )
  ) {
    return true;
  }
  return currentLocale !== nextLocale;
};

export const links: LinksFunction = () => {
  return [
    {
      rel: "preconnect",
      href: "https://edge.disstg.commercecloud.salesforce.com",
      crossOrigin: "anonymous",
    },
    {
      rel: "dns-prefetch",
      href: "https://edge.disstg.commercecloud.salesforce.com",
    },
    { rel: "preconnect", href: "https://cnstrc.com" },
    { rel: "dns-prefetch", href: "https://cnstrc.com" },
    // COM-184: the TrustArc preconnect is NOT here — links() is static
    // (per-route, not per-site), and WDH has no TrustArc mapping
    // (TRUSTARC_DOMAINS.WDH = null), so a static entry would burn a wasted
    // TCP+TLS connection to consent.trustarc.com on every WDH page load. It
    // renders conditionally in Layout's <head> instead, gated on the site's
    // trustArcDomain (same pattern as the GTM dns-prefetch below it).
  ];
};

/**
 * COM-508: resource hints for a BM `Constructor_ServiceUrl` override. The
 * static `links()` above can only point at Constructor's own host; when BM
 * re-points the browser SDK (load-test mock), warm that origin too so
 * client-side autocomplete / recs latency under test isn't skewed by a cold
 * connection. Renders nothing on the default host.
 */
function CioServiceUrlHints({ serviceUrl }: { serviceUrl?: string }) {
  const origin = originOf(serviceUrl);
  if (!origin) return null;
  return (
    <>
      {/* `crossOrigin="anonymous"`: the SDK calls the host via CORS `fetch()`
          with default (omit) credentials, and browsers pool connections by
          credentials mode — a credentialed preconnect would never be reused. */}
      <link rel="preconnect" href={origin} crossOrigin="anonymous" />
      <link rel="dns-prefetch" href={origin} />
    </>
  );
}

function SiteFavicons({ siteId }: { siteId?: string }) {
  const code =
    siteId && siteId in siteIdToCode ? siteIdToCode[siteId as SiteId] : "hsa";
  const base = `/favicons/${code}`;
  return (
    <>
      <link rel="icon" href={`${base}/favicon.ico`} />
      <link
        rel="apple-touch-icon"
        sizes="180x180"
        href={`${base}/apple-touch-icon.png`}
      />
    </>
  );
}

/**
 * The root loader's resolved payload. Exported as a NAMED alias and used as an
 * explicit return annotation on the two hooks below: their inferred return
 * type embeds react-intl message types, and after the @builder.io/sdk-react
 * yarn-patch re-resolution shifted @formatjs hoisting, that inferred type is
 * no longer portable (TS2742 "cannot be named without a reference to
 * @formatjs/…"). A nameable exported alias keeps the declarations emittable.
 */
export type RootLoaderData = Awaited<ReturnType<typeof loader>>;

export const useRootLayoutData = (): RootLoaderData => {
  // const matches = useMatches();
  const data = useRouteLoaderData<typeof loader>("root");
  invariant(data, "useRootLayoutData must be used within root layout");
  return data;
};

export function AppRoot({ children }: { children: React.ReactNode }) {
  const {
    lang,
    site,
    locale,
    translations,
    APP_DEFAULT_LANG,
    isBuilderPreview,
    zendeskSnippetSrc,
    cspNonce,
  } = useLoaderData<typeof loader>();
  const { pathname } = useLocation();
  const buildUrl = createUrlTemplate(locale);
  return (
    <>
      <SiteProvider buildUrl={buildUrl} locale={locale} site={site}>
        <IntlProvider
          onError={err => {
            if (err.code === "MISSING_TRANSLATION") {
              /* NOTE: Remove the console error for missing translations during development, */
              /* as we knew translations would be added later. */
              // console.warn("Missing translation", err.message);
              return;
            }
            console.error(err);
            // throw err;
          }}
          locale={lang}
          messages={translations}
          // For react-intl, the _default locale_ refers to the locale that the inline `defaultMessage`s are written for.
          // NOTE: if you update this value, please also update the following npm scripts in `package.json`:
          // - "extract-default-translations"
          // - "compile-translations:pseudo"
          defaultLocale={APP_DEFAULT_LANG}
        >
          {children}
          <Toaster />
          {/* Segment — first-party SDK init + identify. Suppressed in the
              Builder editor: analytics is pure overhead while editing and
              competes with the SDK editor handshake. */}
          {!isBuilderPreview && (
            <>
              <ClientOnly>{() => <Segment />}</ClientOnly>
              <ClientOnly>{() => <SegmentIdentify />}</ClientOnly>
              {/* V3/V4 fix: analytics.reset() once per sign-out so the ajs
                  identity cookies can't outlive the session they belong to. */}
              <ClientOnly>{() => <SegmentLogoutReset />}</ClientOnly>
              {/* SFCC-11945: programmatic tpa-initial-load marketing consent */}
              <ClientOnly>{() => <TpaConsentTracker />}</ClientOnly>
              <ClientOnly>{() => <SegmentPageView />}</ClientOnly>
              {/* SFRA -> Segment event replay on hybrid pages (SFCC-11725) */}
              <ClientOnly>{() => <SegmentDataLayerInterceptor />}</ClientOnly>
            </>
          )}
          {/* Cart Update — fires on basket mutations (SFCC-10799) */}
          <ClientOnly>{() => <CartUpdateTracker />}</ClientOnly>
          {/* COM-183: Zendesk chat click-to-load facade. Replaces the old
              head-mounted ZendeskSnippetScript (SFCC-11018) — no zdassets
              bytes load until facade click or post-first-interaction idle.
              Suppressed in the Builder editor like the snippet was, and on
              the telehealth intake flow (ET-629). */}
          <ZendeskChatFacade
            src={
              isBuilderPreview || isIntakePath(pathname)
                ? null
                : (zendeskSnippetSrc ?? null)
            }
            nonce={cspNonce}
          />
        </IntlProvider>
      </SiteProvider>
    </>
  );
}

// Explicit annotation for the same TS2742 portability reason as
// useRootLayoutData above — see the RootLoaderData docblock.
export const useRootLoaderData = (): RootLoaderData => {
  const data = useRouteLoaderData<typeof loader>("root");
  invariant(data, "Root loader data is missing");
  return data;
};

export default function App() {
  // Get the locale from the loader
  const {
    IMAGES_HOST,
    cspNonce,
    devConfig,
    constructorio,
    cnstrcUserId,
    gtmRevShare,
    builderio,
    isBuilderPreview,
    pzRefreshWanted,
    pzHits,
    hashedEmail,
    pageContext,
  } = useRootLoaderData();

  // SFCC-11716: signal that this app's initial full-document hydration has
  // committed. `hydrateRoot(document, ...)` (entry.client.tsx) owns the
  // WHOLE document, so any third-party script that injects DOM into <body>
  // before this fires races hydration — React sees DOM it didn't render,
  // throws a hydration mismatch (error #418), and recovers by discarding +
  // re-rendering the document from scratch, wiping out whatever the
  // third-party script just injected. This bit the persona-debug widget
  // (SFCC-11716/11717): its GTM "All Pages" trigger fires as soon as gtm.js
  // loads, which can land before hydration settles, so its floating panel
  // gets wiped seconds after appearing (the console log survives because it
  // already ran; the panel doesn't, because it was never React's DOM to
  // keep). Pushed to `dataLayer` (not a raw DOM event) so MarTech can gate
  // any DOM-injecting GTM Custom HTML tag on a "Custom Event" trigger for
  // `appHydrated` instead of "All Pages" — no change needed to the tag's own
  // script, just the trigger.
  // COM-155: exactly ONE push. The effect was duplicated verbatim when the
  // SFCC-11716 branch landed (6da43219), so every document load pushed
  // `appHydrated` twice and doubled the volume reaching any GTM tag keyed on
  // it. Guarded by app/hec/analytics/app-hydrated-single-push.test.ts.
  useEffect(() => {
    pushDataLayer({ event: "appHydrated" });
  }, []);

  // COM-482: a shell hit that couldn't be completed reloads itself as
  // `/?__ppr=off:<reason>` (cloudflare/ppr.ts). Once that regular document has
  // hydrated, drop the marker from the URL bar so a reload / share / bookmark
  // goes back through the shell. `history.replaceState` rather than a router
  // navigation: nothing should re-run for a cosmetic URL fix, and the router
  // picks the real URL up on the next navigation.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.get("__ppr")?.startsWith("off")) return;
    params.delete("__ppr");
    const search = params.toString();
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`,
    );
  }, []);

  useEffect(() => {
    // setClientUserAttributes is the official Builder Gen 2 SDK helper for the
    // `builder.userAttributes` cookie (same cookie name/shape as the old manual
    // document.cookie write). No canTrack setup needed — the SDK's internal
    // canTrack state defaults to true. NOTE setCanTrack(false) alone is NOT
    // an opt-out for this cookie any more: the parse-time inline write below
    // (COM-101) sits outside the SDK's canTrack gate, so an opt-out must also
    // suppress BuilderUserAttributesInlineScript server-side. Safe to import
    // at module scope: it no-ops server-side (isBrowser() guard) instead of
    // throwing.
    //
    // The parse-time inline script (BuilderUserAttributesInlineScript, first
    // child of <body>) already merged the fresh attribute set into the cookie
    // and deleted `attrRemovals`, so the SDK's inlined personalization
    // scripts could read it during HTML parse (COM-101). This effect still
    // runs after hydration: it files the corrupt-cookie report the snippet
    // left behind (or purges a cookie corrupted since), publishes through
    // the SDK's subscriber (setClientUserAttributes), and re-applies
    // `attrRemovals` — idempotent, and it keeps the SDK's own MERGE
    // ({...current, ...next}) from resurrecting an ended campaign's key.
    // Removals ride as explicit `undefined` per dead key: JSON.stringify then
    // OMITS them. See app/hec/utils/builder-user-attributes.ts.
    safeSetClientUserAttributes(
      {
        ...builderio.userAttributes,
        ...Object.fromEntries(
          builderio.attrRemovals.map(key => [key, undefined]),
        ),
      },
      setClientUserAttributes,
    );
  }, []);

  // Learning Center enter/exit Segment events (HSA only) — ANALYTICS-39.
  // Pure route tracking, no UI; sits with the other location-watchers below.
  useLearningCenterNavTracker();

  // COM-787: keep the `hec_src` attribution cookie alive across soft navs that
  // never reach the session middleware (edge-cache HIT `.data`, no loader).
  useHecSrcSoftNavRefresh(!isBuilderPreview);

  // TPA-14 (SFCC-11069): restore the `tpaBranding` cookie from the `c_TPA`
  // profile attr for a returning authenticated TPA user whose cookie lapsed.
  useRestoreTpaBranding();

  // Provider lives here (not in routes/layout.tsx) so the CIO client is
  // available to subtrees that render outside <Outlet /> — most importantly
  // the <Toaster /> portal used by the add-to-cart flyout recommendations.
  //
  // COM-160: the CIO client SDK dominates the storefront's largest client
  // chunk (the shared ~1.7MB raw / ~530KB gz "search" chunk before this
  // change). A static `constructor.client` import from root.tsx put it on the
  // critical path of every page — fetched, parsed, and executed during initial
  // hydration. Loading it via dynamic import() in a post-hydration idle
  // callback takes it off the critical path entirely. Every consumer reads the
  // client through CIOClientContext with `cio` in its effect deps behind a
  // null guard (the same null they already handle during SSR), so the
  // null → client transition re-runs their effects: the initial-pageview
  // beacons (SFCC-11906 browse/search/item_detail_load) still fire, just a
  // beat after hydration instead of during it. Do not drop them.
  //
  // The state holds the loaded MODULE, not the client: `getCioClient` is then
  // called during render (below), exactly like the pre-COM-160 static import.
  // That render-phase call is what keeps identity sync correct on auth flips —
  // React runs child effects before parent effects, so if `setClientOptions`
  // lived in this effect, a consumer effect keyed on `[cio, isLoggedIn]`
  // (e.g. use-cio-recs) would fire its CIO request BEFORE the singleton's
  // userId updated, attributing it to the stale identity (guest hash after
  // login; the previous user's hash after logout). Render-phase sync also
  // means the import needs no user/key deps at all — no stale closure can
  // seed the wrong user, no matter what changes while the chunk is in flight.
  const [cioSdk, setCioSdk] = useState<typeof CioClientModule | null>(null);
  useEffect(() => {
    let cancelled = false;
    const init = () => {
      import("./utils/constructor.client")
        .then(mod => {
          // `cancelled` collapses StrictMode's double-invoke and guards a
          // resolve after unmount (a stale set into a remounted tree would be
          // harmless anyway — the module namespace is a stable singleton).
          if (cancelled) return;
          setCioSdk(mod);
        })
        .catch((err: unknown) => {
          // Best-effort, single-shot: on a chunk-load failure personalization/
          // beacons degrade to the SSR anonymous experience — never surface
          // it, never retry-loop (this effect runs once per document load).
          console.warn("[cio-client] deferred SDK load failed:", err);
        });
    };
    // requestIdleCallback keeps the SDK's parse/execute out of the hydration
    // window; the timeout bounds beacon lateness on busy pages. Safari has no
    // requestIdleCallback — fall back to a macrotask.
    if (typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(init, { timeout: 2000 });
      return () => {
        cancelled = true;
        window.cancelIdleCallback(id);
      };
    }
    const timeoutId = window.setTimeout(init, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, []);
  // Null on the server and until the deferred chunk lands; afterwards this
  // runs every render, updating the singleton's options via setClientOptions
  // when apiKey/userId change — identical to the previous render-time
  // behavior. (`getCioClient` is typed `| undefined` for SSR safety; the
  // optional call keeps the types honest.)
  // SFCC-11961: latched at first render — the beacon is injected (or not) once
  // per document, so a revalidated loader flipping the pref mid-session must
  // not start SDK tracking alongside an already-loaded beacon (or vice versa).
  const [cioSdkTrackingEnabled] = useState(
    () => constructorio.config.sdkTrackingEnabled === true,
  );
  const cioClient =
    cioSdk?.getCioClient?.(
      constructorio.config.apiKey,
      cnstrcUserId,
      constructorio.config.serviceUrl,
      cioSdkTrackingEnabled,
    ) ?? null;

  return (
    <CIOClientContext value={cioClient}>
      <CioSdkTrackingContext value={cioSdkTrackingEnabled}>
        {IMAGES_HOST && (
          <InjectImagesHostScript IMAGES_HOST={IMAGES_HOST} nonce={cspNonce} />
        )}
        <div
          id="backdrop-quick-search"
          className="absolute top-0 z-50 hidden h-[100%] w-[100%] bg-black bg-opacity-40"
        ></div>
        <DeferH1Seo />
        <PageTypeTracker />
        <ClickSourceTracker />
        {/* SFCC-11961: events the beacon used to send (session_start,
          add-to-cart conversion + its search term); no-ops while the
          beacon → SDK cutover switch is off. */}
        <CioSessionStartTracker />
        <CioConversionTracker />
        <CioSearchTermScope />
        {/* SPA route-change timing — soft-nav replacement for page_load_time
          (SFCC-11889 C7). */}
        <RouteChangePerfTracker />
        {/* P5 D6: AB Tasty PAGEVIEW hits (first paint + every SPA nav), client
          side — replaces the Worker's server PAGEVIEW. Also keeps
          window.__PZ_HITS synced to the loader's pzHits across SPA
          login/logout (the inline script only covers first paint), with the
          sender cuid mirroring the SESSION hashedEmail (pzHits.cuid is
          ajs-derived and lags auth transitions). No-ops unless the config is
          present. */}
        <AbTastyPageviewTracker
          config={pzHits ?? null}
          sessionCuid={hashedEmail ?? null}
        />
        {gtmRevShare === false && <RevShareLinkTracker />}
        {/* COM-661: TPA attribution across the FSA <-> HSA tabs. Deliberately
          NOT behind the `gtmRevShare` gate above — that flag is about
          affiliate links GTM owns, and GTM has no tag for the site switcher.
          See the component docblock. */}
        <TpaSiteSwitchTracker />
        {/* SFCC-11671: suppress link navigation inside the Builder visual editor.
          Mount only for genuine editor requests (`isBuilderPreview` excludes
          shareable `builder.preview` links by design) so the document click
          listener never attaches on live traffic; the guard also re-checks
          `isEditing()` per click as an inner safeguard. */}
        {isBuilderPreview && <BuilderEditorLinkGuard />}
        <AppRoot>
          <BuilderPreviewBanner />
          <Outlet />
        </AppRoot>
        {/* Dev-only tools, loaded AFTER hydration via dynamic import so their
          modules stay out of the always-loaded root chunk (COM-188). On
          production `devConfig` is null, so the imports never fire at all.
          Includes the Personalization Tracker + manifest overlays (their
          heavy bodies are a second-level lazy chunk behind the Dev Panel
          toggle). */}
        {devConfig && <DevTools />}
        {/* P4 §4.5: schedules the off-hot-path traits refresh when SSR
          signaled it (post-hydration idle; one attempt per navigation;
          honors the endpoint's Retry-After backoff via sessionStorage). */}
        <PersonalizationRefreshTrigger wanted={pzRefreshWanted ?? false} />
      </CioSdkTrackingContext>
    </CIOClientContext>
  );
}

/**
 * Reads the deepest matched route's `pageType` (set server-side by each
 * page-rendering loader). Returns `undefined` if no match has set one
 * (resource routes, error boundaries, pages not yet ported). Stable across
 * server + client renders because `useMatches()` returns the same data on
 * both sides.
 *
 * **Loader contract (SFCC-10645)**: page-rendering loaders must `await
 * resolvePageType(...)` and return the resolved string at the top level
 * as `pageType`. A deferred `Promise<string>` won't be picked up — the
 * `typeof === "string"` guard rejects it and the scan falls through to a
 * less specific ancestor (or to `undefined`).
 *
 * **Coverage gap**: returns `undefined` when no match sets `pageType`.
 *
 * SFCC-10645 deliberately stopped there — no fallback — so unported routes
 * showed up as missing data in audits rather than being bucketed into
 * "Other Page". SFCC-12034 reverses that: at 100% rollout the legacy
 * baseline is gone, so an unclassified pageview is no longer a visible
 * audit signal, it is just missing GA4 data. Callers now fall back to the
 * root loader's `fallbackPageType` (`resolvePageType('other')`), matching
 * SFRA's `processDL` (`dataLayerHelper.js:811-821`). The audit signal is
 * largely preserved by the value itself: no Workers route resolves to the
 * `other` bucket on purpose, so "Other Page" mostly still means "route not
 * typed yet".
 *
 * One standing exception: `/favorites` DOES type itself (`favorites.tsx`
 * resolves `"Favorites Page"`), but it pairs `clientLoader.hydrate` with a
 * `HydrateFallback`, and `ServerRouter` strips the server `loaderData` of any
 * route in that shape. So its type is absent at first paint and the fallback
 * fills in "Other Page"; `PageTypeTracker` corrects it once the clientLoader
 * resolves, but `gtm.js` has already read the wrong value. Resolving the type
 * in a surviving ancestor loader would fix it — deliberately out of scope for
 * SFCC-12034, which was scoped to the fallback itself.
 *
 * This hook stays fallback-free; `usePageTypeForRender` layers the fallback
 * and the error-render rules on top.
 */
function usePageType(): string | undefined {
  return matchedPageType(useMatches());
}

/** `isRouteErrorResponse` narrowed to a 404, as `layoutPageType`'s predicate. */
const is404Error = (error: unknown): boolean =>
  isRouteErrorResponse(error) && error.status === 404;

/**
 * The page type for the current render: matched type, else the `other` bucket,
 * with 404s mapped to the `notfound` type and non-404 errors left unset
 * (SFCC-12034 C7 — see `layoutPageType`).
 *
 * Shared by `Layout` (SSR first paint) and `PageTypeTracker` (SPA layout-effect
 * net) so the two can never disagree — `PageTypeTracker` seeds its dedupe ref
 * with this value, so a divergence would make the client immediately re-push
 * over the SSR value.
 *
 * Error detection deliberately does NOT use `useRouteError()`: that resolves
 * `state.errors[<current route id>]`, which from root misses any route with its
 * own `ErrorBoundary`. See `boundaryError`.
 */
function usePageTypeForRender(): string | undefined {
  const routerState = useContext(UNSAFE_DataRouterStateContext);
  const data = useRouteLoaderData<typeof loader>("root");
  const matched = usePageType();
  const error = boundaryError(routerState?.errors);
  return layoutPageType({
    isError: error !== undefined,
    is404: is404Error(error),
    matched,
    // Through the same guards the subscriber path uses, so the root-data leg
    // agrees by construction too (an empty mapping value can't yield `""`
    // here and `undefined` there).
    notFoundPageType: notFoundFromRootData(data),
    fallbackPageType: fallbackFromRootData(data),
  });
}

/**
 * Reads the GA4 productDetail items array set by the PDP loader. Delegates
 * the catch-all dispatcher wrap-shape traversal to `pdpDataFromMatch`, so a
 * future wrap-shape change breaks at compile time in one place instead of
 * silently returning `[]` here. Returns an empty array when no PDP match is
 * active so callers can render unconditionally.
 *
 * SFCC-11006 (ANALYTICS-10).
 */
function usePdpGa4Items(): Ga4ProductDetailItem[] {
  const matches = useMatches();
  const deepest = matches
    .slice()
    .reverse()
    .find(m => {
      const items = pdpDataFromMatch(m)?.analytics?.ga4ProductDetail;
      return Array.isArray(items) && items.length > 0;
    });
  return (
    pdpDataFromMatch(deepest ?? { data: undefined })?.analytics
      ?.ga4ProductDetail ?? []
  );
}

/**
 * Reads the GTM click-source push payload the cart loader resolves from the
 * `?gtms=` querystring (ANALYTICS-41 / SFCC-11026). Cart is a direct route
 * (not via the `$.tsx` dispatcher), so its loader data sits un-wrapped at
 * `m.data.clickSource`. Returns `null` when no active match carries one.
 */
function useCartClickSource(): ClickSource | null {
  const matches = useMatches();
  const match = matches
    .slice()
    .reverse()
    .find(m => {
      const cs = (m.data as { clickSource?: unknown } | undefined)?.clickSource;
      return (
        !!cs &&
        typeof cs === "object" &&
        typeof (cs as ClickSource).event === "string" &&
        typeof (cs as ClickSource).value === "string"
      );
    });
  return (
    (match?.data as { clickSource?: ClickSource } | undefined)?.clickSource ??
    null
  );
}

// `pageTypeFromRouterState` — `usePageType`'s scan applied to raw
// router-subscriber state — lives in `hec/analytics/page-type-selection`
// alongside `matchedPageType` and `layoutPageType`, so the SSR and SPA answers
// stay provably identical under test (root.tsx isn't importable from vitest).
// SFCC-12034 C7.

/**
 * Pushes `ecomm_pagetype` on SPA navigation. `PageTypeInlineScript` handles
 * the first paint (SSR); this covers route changes after hydration.
 *
 * SFCC-11888: the push must be synchronous with the URL change. AB Tasty
 * hooks `history.pushState` and snapshots the dataLayer when it re-evaluates
 * page targeting; the previous post-commit `useEffect` push landed ~93ms
 * after `pushState` (React Router commits navigations inside a
 * `startTransition`, and effects run post-paint), so AB Tasty read the PRIOR
 * page's stale value and its trigger failed. Two layers close that window:
 *
 * 1. **Router subscriber** (primary): `router.subscribe` callbacks run
 *    synchronously in the same task as `history.pushState`
 *    (completeNavigation pushes the URL, then notifies subscribers before
 *    yielding), and the new loader data — hence the new pageType — is
 *    already resolved at that point. Any consumer whose evaluation is
 *    task- or microtask-deferred from `pushState` (AB Tasty's measured
 *    read was ~93ms later) sees the new value. Uses the UNSAFE_ context —
 *    framework mode doesn't expose the router instance publicly; revisit
 *    if RR ever ships a supported subscription API.
 * 2. **Layout effect** (safety net): pre-paint, catches anything the
 *    subscriber misses (context shape change across RR upgrades) with the
 *    same-value guard making it a no-op when the subscriber already pushed.
 *
 * The shared `lastPushed` ref dedupes the two paths and suppresses no-op
 * same-value pushes (e.g. PLP → PLP). Seed = SSR-pushed value, so hydration
 * doesn't double-push. Intermediate subscriber notifications during a
 * navigation (loading states, fetchers) still hold the previous matches +
 * loaderData, so they dedupe away; the value flips exactly once, at the
 * state update that lands the new location.
 */
function PageTypeTracker() {
  const routerContext = useContext(UNSAFE_DataRouterContext);
  // SFCC-12034 C7: the exact expression `Layout` pushes at first paint, so the
  // seed below can't disagree with the SSR value and trigger a redundant
  // re-push on hydration.
  const pageType = usePageTypeForRender();
  // Seed = SSR-pushed value (PageTypeInlineScript). Subsequent SPA-nav
  // transitions push once per value change.
  const lastPushed = useRef<string | undefined>(pageType);
  const rootData = useRouteLoaderData<typeof loader>("root");
  const pageContextConfig = rootData?.pageContext ?? null;
  const siteId = rootData?.site?.id;

  // COM-585: the committed-route publisher rides THIS subscriber rather than
  // opening a second one — the ticket requires one root-owned router observer
  // per SPA document, and two subscriptions would be two observers that could
  // disagree about what committed. Built once (the router is stable for the
  // app's lifetime) and seeded with what the SSR context already published, so
  // hydration cannot republish the landing route.
  //
  // COM-482: on a document shell the initial context minted its id and read
  // its URL in the browser (`resolveInBrowser`), so the seed is what that
  // script parked on `window.__HEC_PAGE_CTX`, not the loader's pair.
  const pprDocument = useIsPprDocument();
  const publisherRef = useRef<PageContextPublisher | null>(null);
  if (pageContextConfig && siteId && !publisherRef.current) {
    const { documentId, documentUrl } =
      pprDocument && typeof window !== "undefined"
        ? documentIdentityFrom(window.__HEC_PAGE_CTX, pageContextConfig)
        : pageContextConfig;
    publisherRef.current = createPageContextPublisher({
      documentId,
      siteDefaults: getSeoConfig(siteId),
      initialKey: navigationKey(
        new URL(documentUrl).pathname,
        new URL(documentUrl).search,
      ),
      initialHref: documentUrl,
      // Canonical client push (SFCC-11011) — guards `window` + lazy-inits the
      // array, the same helper the page-type push below uses.
      sink: pushDataLayer,
      // Park the current context where route-bound event stamping reads it.
      // The SSR inline script seeded this for first paint; this keeps it in
      // step across every soft navigation.
      onPublish: context => {
        window.__HEC_PAGE_CTX = context;
      },
    });
  }

  useEffect(() => {
    const router = routerContext?.router;
    if (!router) return;
    return router.subscribe(state => {
      // COM-585 runs FIRST: C4 requires the context to precede the route-bound
      // events of its own route, and `pushPageType` below is itself read by AB
      // Tasty on the same snapshot.
      const publisher = publisherRef.current;
      if (publisher) {
        // NEVER throw out of the router subscriber. This callback runs on
        // every committed navigation, and the legacy `pushPageType` below
        // depends on reaching the end of it — a throw here would skip it,
        // leaving `ecomm_pagetype` stale and reintroducing exactly the
        // SFCC-11890 AB Tasty bug this code exists to prevent. A missing
        // context is a far better failure than a broken page-type push.
        try {
          publisher.publish({
            pathname: state.location.pathname,
            search: state.location.search,
            href: `${window.location.origin}${state.location.pathname}${state.location.search}${state.location.hash}`,
            // Router state keys loader data by route id, NOT on the match —
            // see `seoFromRouterState`.
            seo: seoFromRouterState(state),
            pageType: pageTypeFromRouterState(state, is404Error),
          });
        } catch {
          // Swallow: analytics context must never disturb navigation.
        }
      }

      const next = pageTypeFromRouterState(state, is404Error);
      if (!next || lastPushed.current === next) return;
      lastPushed.current = next;
      // SFCC-11890: collapse to a single current `ecomm_pagetype` (renaming
      // priors to `prev_ecomm_pagetype`) so AB Tasty's first-match dataLayer
      // read lands on the current page on SPA nav. GTM/GA4 (last-value) are
      // unaffected. Guards `window` + lazy-inits the array internally.
      pushPageType(next);
    });
    // Router instance is stable for the app's lifetime; subscribe once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    if (!pageType) return;
    if (lastPushed.current === pageType) return;
    lastPushed.current = pageType;
    // SFCC-11890: see the router-subscriber path above.
    pushPageType(pageType);
  }, [pageType]);
  return null;
}

/**
 * Pushes the click-source `{event, value}` on SPA navigation to the cart
 * (e.g. the mini-cart "view bag" link is a client `<Link>`, so the SSR inline
 * script never re-renders). Seeds `lastPushed` with the SSR value so hydration
 * doesn't double-push; re-pushes only when the value actually changes — same
 * same-value suppression as `PageTypeTracker` (a repeat landing from the same
 * source won't re-fire, an accepted parity gap for this defer-able ticket).
 *
 * SFCC-11026 (ANALYTICS-41).
 */
function ClickSourceTracker() {
  const clickSource = useCartClickSource();
  const lastPushed = useRef<ClickSource | null>(clickSource);
  useEffect(() => {
    if (!clickSource) return;
    if (
      lastPushed.current?.event === clickSource.event &&
      lastPushed.current?.value === clickSource.value
    )
      return;
    lastPushed.current = clickSource;
    // Canonical client push (SFCC-11011) — guards `window` + initializes
    // `window.dataLayer` internally; same helper `PageTypeTracker` uses.
    pushDataLayer({ event: clickSource.event, value: clickSource.value });
  }, [clickSource]);
  return null;
}

/**
 * Sets the global IMAGES_HOST variable on the client
 * @summary This script is used to set the global IMAGES_HOST variable on the client intentionally kept at root level
 */
const InjectImagesHostScript = memo(function InjectImagesHostScript({
  IMAGES_HOST,
  nonce,
}: {
  IMAGES_HOST: string;
  nonce: string;
}) {
  useMemo(() => {
    // Only run on the client
    if (typeof window !== "undefined") {
      globalThis.IMAGES_HOST = IMAGES_HOST;
    }
  }, [IMAGES_HOST]);

  return (
    <script
      // Browsers security remove nonce attributes when rendrered on the server so we can't hydrate that
      suppressHydrationWarning
      nonce={nonce}
      dangerouslySetInnerHTML={{
        __html: `window.IMAGES_HOST = ${JSON.stringify(IMAGES_HOST)};`,
      }}
    />
  );
});

export const Layout = ({ children }: { children: React.ReactNode }) => {
  const data = useRouteLoaderData<typeof loader>("root");
  const layoutData = useRouteLoaderData<typeof layoutLoader>("routes/layout");
  const { lang } = data || {};
  const error = useRouteError();
  const isError = !!error;
  // Override the matched-route pageType with `notfound` ONLY for 404 responses
  // (route loader threw `Response("Not Found", { status: 404 })`). Other
  // errors (5xx, generic crashes) leave pageType unset so the audit shows
  // the absence rather than mis-bucketing into "Page Not Found".
  //
  // SFCC-12034 C7: on a normal render, a route that sets no pageType falls back
  // to the `other` bucket (SFRA `processDL` parity) instead of pushing nothing.
  // Error renders are excluded on purpose — see `layoutPageType`. Note this
  // detects errors caught at ANY boundary, unlike the `isError` above (which
  // gates rendering and is scoped to root by `useRouteError`).
  const pageType = usePageTypeForRender();
  // COM-585: the ONE initial context, built here so it renders inside the
  // contiguous inline-script run ahead of GTM and AB Tasty. `null` whenever the
  // contract is suppressed (flag off / site not allowlisted / bot / Builder
  // editor), in which case `PageTypeInlineScript` emits exactly what it always
  // did. Title comes from the SAME match scan the client publisher uses, so the
  // SSR and SPA halves cannot disagree.
  //
  // COM-482: a document shell is replayed to every guest, so on one the two
  // per-request values (the per-document id and the URL) are left empty here
  // and resolved by the script in the browser — see `resolveInBrowser` on
  // `PageTypeInlineScript`. The script cannot move into the shopper-scripts
  // hole: a hole resumes after the prelude's GTM container is on the wire.
  const pprDocument = useIsPprDocument();
  const initialPageContextMatches = useMatches();
  const initialPageContext = useMemo(() => {
    const pc = data?.pageContext;
    const siteId = data?.site?.id;
    if (!pc || !siteId) return null;
    return buildPageContext({
      navigationId: pprDocument
        ? ""
        : navigationIdAt(pc.documentId, INITIAL_NAVIGATION_ORDINAL),
      pageLocation: pprDocument ? "" : pc.documentUrl,
      pageTitle: resolvePageTitle(
        matchedSeo(initialPageContextMatches),
        getSeoConfig(siteId),
      ),
      pageType,
    });
  }, [
    data?.pageContext,
    data?.site?.id,
    initialPageContextMatches,
    pageType,
    pprDocument,
  ]);
  const ga4Items = usePdpGa4Items();
  const cartClickSource = useCartClickSource();
  // SEO-11: look up per-site verification tokens once; undefined on error pages
  // where data is null. bySiteId throws on unknown siteId — safe here because
  // worker.server.ts only resolves HSASTORE | FSASTORE | WDH.
  const siteVerification = data?.site?.id
    ? bySiteId(data.site.id, SEO_VERIFICATION)
    : undefined;

  return (
    <html
      lang={lang}
      className={cn(
        "h-full",
        data?.site?.id,
        // COM-184: activates the flag-gated mobile consent-banner
        // size-bounding CSS in app/styles/app.css. Dark by default
        // (WORKERS_TRUSTARC_SLIM_BANNER_MODE fail-closed) — enabling requires
        // legal/compliance sign-off.
        data?.trustArcSlimBanner && "trustarc-slim-banner",
      )}
      suppressHydrationWarning
    >
      <head suppressHydrationWarning>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        {/* SEO-14: twitter:card — same value on all pages, all sites */}
        <meta name="twitter:card" content="summary_large_image" />
        {/* SEO-11: per-site Google + FB verification (values from SFCC BM 2026-04-23) */}
        {siteVerification && (
          <>
            <meta
              name="google-site-verification"
              content={siteVerification.googleVerification}
            />
            {siteVerification.fbAdmins && (
              <meta property="fb:admins" content={siteVerification.fbAdmins} />
            )}
          </>
        )}
        <link rel="preconnect" href="https://images.ctfassets.net/" />
        {/* DeferSeo renders in <body> (see below), NOT here: its <title>/<meta>/
            <link> still hoist to <head>, but its JSON-LD <script> would be a
            positional head script that the 3p flood clobbers on hydrate. */}
        <Meta />
        {/* Per-site favicon. Assets live in `public/favicons/<site>/`. Defaults
            to HSA when site isn't resolved (matches the worker.server.ts
            loopback default). */}
        <SiteFavicons siteId={data?.site?.id} />
        {/* All meta exports on all routes will go here */}
        {/* All link exports on all routes will go here */}
        <Links />
        {/* GTM dns-prefetch — hoistable, stays in <head>. The analytics inline
            scripts themselves now render at the TOP of <body> (see below), out
            of <head> entirely: React 19 head hoisting then has no positional
            script in the head to shift, and a 3p that injects into <head> can't
            interleave with them. */}
        {data?.gtm?.config?.containerId && (
          <link
            rel="dns-prefetch"
            href={`https://www.googletagmanager.com/gtm.js?id=${data.gtm.config.containerId}`}
          />
        )}
        <CioServiceUrlHints
          serviceUrl={data?.constructorio?.config?.serviceUrl}
        />
        {/* COM-184: TrustArc preconnect — the banner text is the homepage LCP
            element, so shaving DNS+TCP+TLS off the consent-script fetch is on
            the LCP critical path. Same host serves the notice script AND the
            notice-config bundle it fetches next (only the `domain=` query
            param differs per site). Rendered here (hoistable, stays in <head>)
            rather than in the static links() export so it's gated per-site:
            WDH has no TrustArc mapping (TRUSTARC_DOMAINS.WDH = null), never
            loads the script, and must not burn a connection to
            consent.trustarc.com. */}
        {data?.trustArcDomain && (
          <>
            <link rel="preconnect" href={TRUSTARC_HOST} />
            <link rel="dns-prefetch" href={TRUSTARC_HOST} />
          </>
        )}
        {/* Data-dependent dataLayer pushes + the CIO beacon run CLIENT-ONLY
            (DataLayerClientPushes useEffect — renders null), so no data-driven
            <script> sits in the hydrated <head>. */}
        <DataLayerClientPushes
          ga4Items={ga4Items}
          clickSource={cartClickSource}
          heapId={data?.heapId ?? null}
          isAuthenticated={data?.isRegistered ?? false}
          userId={data?.userId ?? null}
          hashedEmail={data?.hashedEmail ?? null}
          customerLoyalty={data?.customerLoyalty ?? false}
          customerGroups={data?.customerGroups ?? []}
          cnstrcUserId={data?.cnstrcUserId ?? null}
          beaconBundleName={
            data?.constructorio?.config?.beaconBundleName ?? null
          }
          nonce={data?.cspNonce}
        />
        {/* SFCC-11804: GA4_productDetail on SPA navigation to a PDP.
            DataLayerClientPushes (above) fires only on first paint / hard load;
            this re-pushes per viewed product on client-side navigation. */}
        <ProductDetailTracker ga4Items={ga4Items} />
        {/* COM-417 item 2: identity keys on SPA login (drawer fetcher) and
            logout (SPA nav) — DataLayerClientPushes writes them on hard load
            only, so without this the previous customer kept being reported
            after logout. Same identity props it receives; clears on sign-out.

            Gated on `data`: on a root ErrorBoundary render `useRouteLoaderData`
            returns undefined, and defaulting to the signed-out shape there
            would push a LOGOUT for a still-signed-in shopper — the same
            wrong-identity class of bug this tracker exists to fix. No data =
            nothing known = push nothing. */}
        {data && (
          <IdentityTracker
            isAuthenticated={data.isRegistered}
            userId={data.userId}
            hashedEmail={data.hashedEmail}
            heapId={data.heapId}
            customerLoyalty={data.customerLoyalty}
            customerGroups={data.customerGroups}
          />
        )}
        {/* COM-183: the Zendesk widget no longer loads here. The click-to-load
            facade (ZendeskChatFacade, rendered in AppRoot's body subtree)
            owns the snippet injection — nothing from zdassets is fetched
            until the facade is clicked or an idle slot after the first user
            interaction. */}
      </head>
      <body
        suppressHydrationWarning
        className="rendered relative min-h-full bg-background text-base tracking-normal [&_iframe]:pointer-events-auto"
      >
        {/* Analytics inline scripts as the FIRST children of <body>, moved out
            of <head>: React 19 head hoisting can't shift them (the head holds
            only hoistables now) and head-injected 3p scripts can't interleave
            with them. They stay a contiguous run with no hoistable between, run
            at body parse-time (before hydration — a touch later than in <head>,
            but before any consent-gated tag fires). Order is load-bearing:
            consent defaults (ANALYTICS-02), then the ecomm_pagetype push
            (SFCC-11888 — must precede gtm.js evaluation AND the synchronous
            AB Tasty script, both of which read it at evaluation time), then
            the GTM container (ANALYTICS-01/35); AB Tasty + Bloomreach follow.
            COM-101: `builder.userAttributes` MUST be written first — the
            SDK inlines personalization scripts inside later `<Content>`
            that read the cookie during HTML parse and hide non-winning
            variants. The root useEffect setter is post-hydration, too late.
            FlagshipVidInlineScript has no dataLayer dependency; it just
            needs to land before any AB Tasty Universal Collect hit sender
            reads window.__FLAGSHIP_VID. */}
        {/* COM-482 PPR: the run is split in two. Scripts whose inputs are
            site-level (identical for every guest — the shell fingerprint pins
            them) render first and live in the cached shell, so on a shell hit
            they still execute at body parse-time, before any Builder content.
            Scripts carrying per-shopper values (vid, traits, pz config,
            persona payload) render inside the hole, which the shell resumes
            AFTER the prelude body. Ordering invariants this keeps everywhere:
            consent defaults → ecomm_pagetype push → GTM → AB Tasty (all in
            the static run, in order); FlagshipVid before the client hit
            sender (post-hydration); PersonaDebug before GTM on the regular
            path and irrelevant on a hit (`?pers_debug` is a query → never a
            hit). The one invariant a hit relaxes: `builder.userAttributes`
            is written by the hole, i.e. after the prelude's Builder content
            parsed — its inline personalization/A-B scripts read the cookie
            a returning guest already carries from the previous page, the
            hole then refreshes it before hydration, and the SDK re-reads the
            cookie at hydration (`PersonalizationContainer` initialises from
            `getUserAttributes()`) so a changed attribute set corrects the
            variant on hydrate — same one-page lag as a guest-HTML-cache HIT.
            A cookieless first visit is a hit too (the seam pre-mints its
            session) and carries no cookie yet: its parse-time choice is the
            default variant until hydration corrects it. Open item — see
            "Open before the production canary" in docs/ppr-shell.md. */}
        <PprHole name="root-shopper-scripts">
          {data?.builderio?.userAttributes && (
            <BuilderUserAttributesInlineScript
              attributes={data.builderio.userAttributes}
              removals={data.builderio.attrRemovals}
              nonce={data.cspNonce}
            />
          )}
          <FlagshipVidInlineScript
            vid={data?.abTastyVid ?? null}
            nonce={data?.cspNonce}
          />
          {/* P5 D6: enables the client hit-sender; null (renders nothing)
            unless WORKERS_ABTASTY_MODE is on. Independent of the legacy tag,
            which rides WORKERS_ABTASTY_TAG_SUPPRESS_MODE (§5.5 re-amendment
            2026-09-04 — the runtimes coexist). Reads nothing from dataLayer;
            keeps the inline run contiguous. */}
          <PzHitsConfigInlineScript
            config={data?.pzHits ?? null}
            nonce={data?.cspNonce}
          />
          {/* SFCC-11717: expose window.__PERSONA_TRAITS for the external MarTech
            persona-debug widget. Renders only under ?pers_debug=true (payload
            null otherwise). Placed before the GTM container so the global
            exists before any GTM-fired widget tag reads it. The dev echo is
            non-production only (devConfig gate) — clearly labeled so it's never
            mistaken for the real widget-rendered console output.
            SFCC-11716: the widget's own DOM injection (its floating panel)
            must NOT fire on GTM's "All Pages" trigger — see the `appHydrated`
            dataLayer push in the App() component below for why. */}
          <PersonaDebugInlineScript
            payload={data?.personaDebug ?? null}
            nonce={data?.cspNonce}
          />
          {data?.devConfig && (
            <PersonaDebugDevEcho payload={data?.personaDebug ?? null} />
          )}
        </PprHole>
        {/* COM-67: installs the re-entrant navigation guard BEFORE hydration.
            The SSR `<a href>` is clickable as soon as it parses, while the
            React-side guard only exists once React hydrates (~0.3-0.8s later),
            and a committed same-origin navigation drops the shopper into the
            next document's unguarded window. Registers one bubble-phase
            listener and touches no shared global, so it is order-insensitive
            relative to the analytics scripts below — but keep it early. */}
        <DocumentNavGuardInlineScript nonce={data?.cspNonce} />
        {/* COM-107: inside a TPA partner iframe every link opens a new tab on
            the bare host. Inline and early for the nav guard's reason: SSR
            anchors are clickable before hydration. Bails unless framed.
            Host-level input: the PPR shell is keyed per host. */}
        {layoutData?.isTpaSubdomain && (
          <script
            suppressHydrationWarning
            nonce={data?.cspNonce}
            dangerouslySetInnerHTML={{ __html: TPA_ESCAPE_INLINE_SOURCE }}
          />
        )}
        <ConsentDefaultsInlineScript nonce={data?.cspNonce} />
        {/* COM-184: TrustArc bootstrap — injects the consent-notice script at
            body parse-time (async + fetchpriority=high) instead of waiting for
            the Builder block's post-hydration useEffect; the banner text is
            the homepage LCP element. MUST stay after
            ConsentDefaultsInlineScript (consent defaults precede TrustArc's
            consent updates — the analytics-foundation.md invariant) and is an
            inline positional script, keeping the run contiguous. Suppressed in
            the Builder editor: the banner overlay fights visual editing (the
            TrustArcBanner block's own useEffect still covers the editor). */}
        {!data?.isBuilderPreview && (
          <TrustArcBootstrapInlineScript
            domain={data?.trustArcDomain}
            nonce={data?.cspNonce}
          />
        )}
        <PageTypeInlineScript
          pageType={pageType}
          nonce={data?.cspNonce}
          initialPageContext={initialPageContext}
          resolveInBrowser={pprDocument}
        />
        {/* SFCC-12091: hec_src entry-attribution cookie client backstop —
            covers the edge-cache-HIT landings the session-management
            middleware never sees, and the PPR shell hits whose middleware
            Set-Cookie can't follow the committed headers (cloudflare/ppr.ts
            relies on this script being in the shell). Before the GTM
            container so the cookie exists when GTM's re-supply variables
            first read it; no dataLayer dependency. Suppressed in the Builder
            editor like GTM (editor iframe loads must not plant/refresh
            attribution). */}
        {!data?.isBuilderPreview && (
          <HecSrcInlineScript nonce={data?.cspNonce} />
        )}
        {/* GTM container + the tag cascade it injects (Heap, Bing UET, LinkedIn,
            DoubleClick, FullStory, …) is suppressed in the Builder editor: none
            of it is needed there, and the cascade contends for the main thread
            and network during the SDK's editor-handshake window.
            `isRegistered` is false for every PPR-eligible request (guests
            only), so the shell's copy is right for every hit. */}
        {!data?.isBuilderPreview && (
          <GtmContainerScript
            containerId={data?.gtm?.config?.containerId ?? ""}
            isRegistered={data?.isRegistered ?? false}
            nonce={data?.cspNonce}
          />
        )}
        {/* SFCC-11023 ANALYTICS-31: AB Tasty experiment script — synchronous
            (no async/defer) so it can rewrite the DOM for experiment-targeting
            before hydration. That synchronous fetch+exec also blocks the parser
            before the React bundle, so it's suppressed in the Builder editor.
            COM-162: when WORKERS_ABTASTY_ASYNC_MODE=on (fail-closed; needs
            product/martech sign-off) the tag loads async behind a 500 ms-capped
            anti-flicker body-hide instead — the parser is never blocked and
            the ecomm_pagetype push above still precedes AB Tasty evaluation
            (it executes strictly earlier in this same inline run). Flag off ⇒
            the literal <script> below, byte-identical to the shipped HTML. */}
        {!data?.isBuilderPreview &&
          data?.abTasty?.config?.scriptUrl &&
          (data?.abTasty?.asyncTag ? (
            <AbTastyAsyncTagInlineScript
              scriptUrl={data.abTasty.config.scriptUrl}
              nonce={data.cspNonce}
            />
          ) : (
            <script
              src={data.abTasty.config.scriptUrl}
              nonce={data.cspNonce}
              suppressHydrationWarning
            />
          ))}
        {/* SFCC-11019 ANALYTICS-34: Bloomreach (Exponea) — inline globals +
            deferred external snippet; gated per-site (FSA/HSA on, WDH off).
            SFCC-11609: suppressed in the Builder editor/preview so the Exponea
            email-popup web layer doesn't overlay and block visual editing.
            `userId` is empty for every guest, so it is shell-safe. */}
        {!data?.isBuilderPreview && (
          <BloomreachScripts
            config={data?.bloomreach?.config}
            userId={data?.customer?.customerNo ?? ""}
            nonce={data?.cspNonce}
          />
        )}
        {/* DeferSeo renders here in <body>, not <head>. React 19 hoists its
            <title>/<meta>/<link> to <head> (SEO intact — crawlers read them from
            the streamed head), while its JSON-LD <script type="application/ld+json">
            — a positional script — renders in the body (valid structured-data
            location). This keeps the <head> free of ANY React positional
            <script>, which the third-party script flood (GTM, gtag, Bing UET,
            Heap, Exponea, AB Tasty) would otherwise clobber on hydrate → the
            structural mismatch. */}
        {!isError && <DeferSeo />}
        {children}

        {/* Manages scroll position for client-side transitions */}
        {/* If you use a nonce-based content security policy for scripts, you must provide the `nonce` prop. Otherwise, omit the nonce prop as shown here. */}
        <ScrollRestoration nonce={data?.cspNonce} />
        {/* Script tags go here */}
        {/* If you use a nonce-based content security policy for scripts, you must provide the `nonce` prop. Otherwise, omit the nonce prop as shown here. */}
        {/* COM-523: preloads the entry + matched route modules, and the rest
            of their import graph at low priority; see LeanScripts for why. */}
        <LeanScripts nonce={data?.cspNonce} />
        {/* Sets up automatic reload when you change code */}
        {/* and only does anything during development */}
        {/* If you use a nonce-based content security policy for scripts, you must provide the `nonce` prop. Otherwise, omit the nonce prop as shown here. */}
      </body>
    </html>
  );
};

/**
 * This component is used to remove the no-transition class from the body when the location changes.
 * enabled css transitions
 */
